import { useCallback, useEffect, useRef } from 'react';
import { useQuotationDraftStore } from '../../store/quotationDraftStore';
import { QuotationStatus, type QuotationDraft } from '../../types/quotation.types';
import { ReviewSourcePanel } from './ReviewSourcePanel';
import { ReviewLineItemsList } from './ReviewLineItemsList';
import { ReviewExtractionHeader } from './ReviewExtractionHeader';

interface QuotationReviewViewProps {
  draft: QuotationDraft;
}

// Source-pane width as a fraction of the split. Source can shrink to 15% so the
// line-items pane can be dragged up to 85% to read the table — 86d3fcbte. Kept
// in sync with the Sales Order review split.
const MIN_RATIO = 0.15;
const MAX_RATIO = 0.85;

export function QuotationReviewView({ draft }: QuotationReviewViewProps) {
  const ratio = useQuotationDraftStore((s) => s.reviewSourceColRatio);
  const setRatio = useQuotationDraftStore((s) => s.setReviewSourceColRatio);
  const setReviewMode = useQuotationDraftStore((s) => s.setReviewMode);
  const activeId = useQuotationDraftStore((s) => s.activeId);

  const splitRef = useRef<HTMLDivElement>(null);
  const enteredWithIdRef = useRef<string | null>(activeId);

  // Auto-exit if the active draft changes underneath us (e.g. external state mutation,
  // or a draft is closed while we're reviewing). Exit-on-navigate is handled by the
  // workspace detail panel, which calls setReviewMode(false) whenever the selected row
  // changes; we don't add a defensive unmount cleanup here because StrictMode's simulated
  // unmount would flip review off immediately after entering it in dev.
  useEffect(() => {
    if (enteredWithIdRef.current && activeId !== enteredWithIdRef.current) {
      setReviewMode(false);
    }
  }, [activeId, setReviewMode]);

  const handleDragStart = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      const container = splitRef.current;
      if (!container) return;
      const handle = e.currentTarget;

      // 86d3fcbte — pointer CAPTURE on the handle, not window mouse listeners.
      // The source pane embeds the PDF in an <object> (its own browsing context);
      // a plain window 'mouseup' fired over that object never reaches us, so the
      // drag never ended and the divider kept following the cursor after release.
      // setPointerCapture redirects every pointermove/up to the handle regardless
      // of what sits underneath, and pointercancel/up reliably end the drag.
      handle.setPointerCapture(e.pointerId);

      const onMove = (ev: PointerEvent) => {
        const rect = container.getBoundingClientRect();
        if (rect.width === 0) return;
        const r = (ev.clientX - rect.left) / rect.width;
        if (Number.isFinite(r)) setRatio(r);
      };
      const stop = (ev: PointerEvent) => {
        handle.releasePointerCapture?.(ev.pointerId);
        handle.removeEventListener('pointermove', onMove);
        handle.removeEventListener('pointerup', stop);
        handle.removeEventListener('pointercancel', stop);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };

      handle.addEventListener('pointermove', onMove);
      handle.addEventListener('pointerup', stop);
      handle.addEventListener('pointercancel', stop);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    },
    [setRatio],
  );

  const clampedRatio = Math.max(MIN_RATIO, Math.min(MAX_RATIO, ratio));

  return (
    <div
      ref={splitRef}
      className="flex h-full w-full flex-col xl:flex-row"
      style={{ '--source-col': `${(clampedRatio * 100).toFixed(2)}%` } as React.CSSProperties}
    >
      {/* Source panel — full width below xl, 75% (approx) on xl.
          `min-w-0` is critical: without it, a wide spreadsheet's intrinsic content
          width would push this panel beyond its flex-basis and squeeze the line
          items panel to zero. */}
      <div className="relative h-[60vh] w-full shrink-0 grow-0 basis-auto border-b xl:h-auto xl:min-w-0 xl:basis-[var(--source-col)] xl:border-b-0 xl:border-r">
        <ReviewSourcePanel draft={draft} />
      </div>

      {/* Drag handle — visible only on xl+ */}
      <div
        role="separator"
        aria-orientation="vertical"
        className="hidden w-1 shrink-0 cursor-col-resize touch-none bg-border transition-colors hover:bg-primary/40 active:bg-primary xl:block"
        onPointerDown={handleDragStart}
        title="Drag to resize"
      />

      {/* Line items panel — full width below xl, remainder on xl. AutoDrafts get the
          extraction-status header + retry button on top of the list; other drafts skip it. */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col xl:flex-1">
        {draft.status === QuotationStatus.AutoDraft && draft.serverId != null && (
          <ReviewExtractionHeader draft={draft} />
        )}
        <div className="min-h-0 flex-1">
          <ReviewLineItemsList draft={draft} />
        </div>
      </div>
    </div>
  );
}
